import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { backendApiUrl } from '../../config/backendApiUrl';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Tooltip,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  ViewModule as CardsIcon,
  ViewList as TableIcon,
  ViewAgenda as AccordionIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  parseAlgoConfig,
  getFullKey,
  shouldUseSelect,
  getSelectOptions,
  filterSections,
  getSectionDisplayName
} from '../../utils/algoConfigParser';
import FieldRenderer from '../../components/config/FieldRenderer';
import {
  mergeConfigWithMetadata,
  groupByMetadata,
  flattenConfig,
  filterParameters,
  filterParametersByValue,
  getSections,
  getGroupsInSection,
  getParametersInGroup,
  isParameterVisible
} from '../../utils/configMetadataUtils';

// Lightweight authenticated fetch helper for endpoints that return non-JSON (e.g., text configs)
const authFetch = async (url, options = {}) => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  const token = await user.getIdToken();
  const headers = {
    ...(options.headers || {}),
    Authorization: `Bearer ${token}`,
  };
  return fetch(backendApiUrl(url), { ...options, headers });
};

/**
 * Config Editor Page
 * Dynamic editor for Algo device configuration files
 * 
 * Features:
 * - Parse and display config in grouped tables
 * - Edit individual parameters
 * - Apply changes via REST API
 * - Track modifications
 * - Search/filter parameters
 */
export default function ConfigEditorPage({
  // Props for workspace mode
  deviceId: propDeviceId = null,
  deviceName: propDeviceName = null,
  deviceIp: propDeviceIp = null,
  tenantId: propTenantId = null,
  configData: propConfigData = null,
  lastLoadedAt: propLastLoadedAt = null,
  onConfigLoaded = null,
  onUnsavedChanges = null,
  isWorkspaceMode = false,
  workspaceTabId: propWorkspaceTabId = null,
  isActive: propIsActive = false
} = {}) {
  const { deviceId: paramDeviceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Use props if in workspace mode, otherwise use URL params
  const deviceId = isWorkspaceMode ? propDeviceId : paramDeviceId;
  const deviceIp = isWorkspaceMode ? propDeviceIp : (searchParams.get('deviceIp') || '');
  const deviceName = isWorkspaceMode ? propDeviceName : (searchParams.get('deviceName') || deviceId);
  const tenantId = isWorkspaceMode ? propTenantId : (searchParams.get('tenantId') || null);
  const workspaceTabId = isWorkspaceMode ? (propWorkspaceTabId || deviceIp || deviceId) : null;
  const isActive = Boolean(isWorkspaceMode ? propIsActive : true);
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawConfig, setRawConfig] = useState('');
  const [originalConfig, setOriginalConfig] = useState({});
  const [editedConfig, setEditedConfig] = useState({});
  const [editingCell, setEditingCell] = useState(null); // { section, key }
  const [applyingChanges, setApplyingChanges] = useState({}); // { 'section.key': boolean }
  const [searchQuery, setSearchQuery] = useState('');
  const [valueSearchQuery, setValueSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedSections, setExpandedSections] = useState({});
  const [viewMode, setViewMode] = useState('cards'); // 'cards', 'table', 'accordion'
  const [savingSection, setSavingSection] = useState({}); // { section: boolean }
  const [refreshingSection, setRefreshingSection] = useState({}); // { section: boolean }
  const hasLoadedInitialConfig = useRef(false); // Track if we've loaded config to prevent reloads
  const perfRef = useRef({ lastActiveAt: null });

  const isPerfDebugEnabled = useCallback(() => {
    try {
      if (typeof window !== 'undefined' && window.__DEBUG_CONFIG_WORKSPACE__ === true) return true;
      return localStorage.getItem('DEBUG_CONFIG_WORKSPACE') === 'true';
    } catch (_e) {
      return false;
    }
  }, []);
  
  // Metadata-driven state
  const [mergedParams, setMergedParams] = useState([]); // Array of { param, value, meta }
  const [flatConfig, setFlatConfig] = useState({}); // Flat config for visibility checks
  const originalFlatConfig = useMemo(() => {
    try {
      return flattenConfig(originalConfig);
    } catch (_e) {
      return {};
    }
  }, [originalConfig]);

  useEffect(() => {
    if (!isWorkspaceMode) return;
    if (!isPerfDebugEnabled()) return;
    if (!isActive) return;

    const now = performance.now();
    perfRef.current.lastActiveAt = now;

    const lastClick = (() => {
      try {
        return window.__CONFIG_WS_LAST_CLICK__ || null;
      } catch (_e) {
        return null;
      }
    })();

    if (lastClick && lastClick.to === workspaceTabId && typeof lastClick.t0 === 'number') {
      console.info('[CONFIG_WS_PERF] editor_active', { tabId: workspaceTabId, dt_ms: now - lastClick.t0 });
    } else {
      console.info('[CONFIG_WS_PERF] editor_active', { tabId: workspaceTabId, t: now });
    }
  }, [isActive, isPerfDebugEnabled, isWorkspaceMode, workspaceTabId]);

  /**
   * Fetch and parse config file
   */
  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let parsed = null;
      let lastErr = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        const basePath = isWorkspaceMode
          ? `/api/v2/admin/devices/tenant-device/${encodeURIComponent(deviceId)}/config`
          : `/api/v2/admin/devices/${encodeURIComponent(deviceId)}/config`;
        const query = isWorkspaceMode && tenantId
          ? `?tenantId=${encodeURIComponent(tenantId)}`
          : '';
        const res = await authFetch(`${basePath}${query}`);
        if (res.ok) {
          const text = await res.text();
          setRawConfig(text);
          parsed = parseAlgoConfig(text);
          break;
        } else {
          lastErr = new Error(`Failed to fetch config: ${res.status} ${res.statusText}`);
          if (attempt < 3) {
            await new Promise((r) => setTimeout(r, 1500));
          }
        }
      }
      if (!parsed) {
        throw lastErr || new Error('Failed to load config');
      }
      setOriginalConfig(parsed);
      setEditedConfig(JSON.parse(JSON.stringify(parsed)));
      const firstSection = Object.keys(parsed)[0];
      if (firstSection) {
        setExpandedSections({ [firstSection]: true });
      }
      
      // Compute metadata-driven state
      const flat = flattenConfig(parsed);
      setFlatConfig(flat);
      const merged = mergeConfigWithMetadata(flat);
      setMergedParams(merged);
      
      // Notify parent in workspace mode
      if (isWorkspaceMode && onConfigLoaded) {
        onConfigLoaded(workspaceTabId || deviceIp || deviceId, parsed);
      }
      
      toast.success('Config loaded successfully');
    } catch (err) {
      console.error('Failed to load config:', err);
      setError(err.message);
      toast.error(`Failed to load config: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [deviceId, isWorkspaceMode, onConfigLoaded, tenantId]);

  // Reset load flag when device changes
  useEffect(() => {
    hasLoadedInitialConfig.current = false;
  }, [deviceId]);

  // Load config on mount or when device changes
  useEffect(() => {
    console.log('[CONFIG-EDITOR] Config load effect triggered:', {
      isWorkspaceMode,
      hasPropConfigData: !!propConfigData,
      hasLoadedInitialConfig: hasLoadedInitialConfig.current,
      deviceId,
      timestamp: new Date().toISOString()
    });
    
    // CRITICAL: Only load config once per device to prevent wiping user edits
    if (hasLoadedInitialConfig.current) {
      console.log('[CONFIG-EDITOR] Skipping config reload - already loaded for this device');
      return;
    }
    
    if (isWorkspaceMode && propConfigData) {
      // Use provided config data in workspace mode
      console.log('[CONFIG-EDITOR] Using provided config data (workspace mode)');
      setOriginalConfig(propConfigData);
      setEditedConfig(JSON.parse(JSON.stringify(propConfigData)));
      const firstSection = Object.keys(propConfigData)[0];
      if (firstSection) {
        setExpandedSections({ [firstSection]: true });
      }
      
      // Compute metadata-driven state
      const flat = flattenConfig(propConfigData);
      setFlatConfig(flat);
      const merged = mergeConfigWithMetadata(flat);
      setMergedParams(merged);
      
      setLoading(false);
      hasLoadedInitialConfig.current = true;

      try {
        if (isPerfDebugEnabled() && isActive) {
          const now = performance.now();
          const lastClick = window.__CONFIG_WS_LAST_CLICK__ || null;
          if (lastClick && lastClick.to === workspaceTabId && typeof lastClick.t0 === 'number') {
            console.info('[CONFIG_WS_PERF] editor_ready', { tabId: workspaceTabId, mode: 'prop', dt_ms: now - lastClick.t0 });
          } else {
            console.info('[CONFIG_WS_PERF] editor_ready', { tabId: workspaceTabId, mode: 'prop', t: now });
          }
        }
      } catch (_e) {}
    } else if (!isWorkspaceMode || !propConfigData) {
      // Fetch config in standalone mode or if no data provided
      console.log('[CONFIG-EDITOR] Fetching config (standalone mode or no prop data)');
      loadConfig();
      hasLoadedInitialConfig.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, isWorkspaceMode, propConfigData]);

  /**
   * Apply a single parameter change
   */
  const applyChange = async (section, subKey) => {
    const fullKey = getFullKey(section, subKey);
    const newValue = editedConfig[section][subKey];
    const originalValue = originalConfig[section][subKey];
    
    console.log('[CONFIG-EDITOR] applyChange called:', {
      fullKey,
      section,
      subKey,
      originalValue,
      newValue,
      deviceId,
      timestamp: new Date().toISOString()
    });
    
    // Check if actually changed
    if (newValue === originalValue) {
      console.log('[CONFIG-EDITOR] No changes detected, aborting apply');
      toast.info('No changes to apply');
      return;
    }
    
    console.log('[CONFIG-EDITOR] Starting apply request to backend');
    setApplyingChanges(prev => ({ ...prev, [fullKey]: true }));
    
    try {
      const url = `/api/v2/admin/devices/${encodeURIComponent(deviceId)}/settings`;
      const payload = { [fullKey]: newValue };
      console.log('[CONFIG-EDITOR] Sending PUT request:', { url, payload });
      
      const res = await authFetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('[CONFIG-EDITOR] Received response:', { status: res.status, ok: res.ok });
      const result = await res.json().catch(() => ({}));
      console.log('[CONFIG-EDITOR] Response body:', result);

      if (!res.ok) {
        throw new Error(result.error || `HTTP ${res.status}`);
      }

      const perParamResults = result?.data?.results || {};
      const paramResult = perParamResults[fullKey];

      if (paramResult?.ok || result?.success) {
        console.log('[CONFIG-EDITOR] Apply succeeded, updating originalConfig');
        setOriginalConfig(prev => ({
          ...prev,
          [section]: {
            ...prev[section],
            [subKey]: newValue
          }
        }));

        toast.success(`✓ Updated ${fullKey}`);
      } else {
        console.error('[CONFIG-EDITOR] Apply failed, result:', { paramResult, result });
        throw new Error((paramResult && paramResult.error) || result?.error || 'Update failed');
      }
    } catch (err) {
      console.error('[CONFIG-EDITOR] Apply error:', err);
      toast.error(`Failed to update ${fullKey}: ${err.message}`);
    } finally {
      console.log('[CONFIG-EDITOR] Apply complete, clearing applying state');
      setApplyingChanges(prev => ({ ...prev, [fullKey]: false }));
    }
  };

  /**
   * Handle value change in editor
   */
  const handleValueChange = (section, subKey, newValue) => {
    const fullKey = getFullKey(section, subKey);
    const oldValue = editedConfig[section]?.[subKey];
    console.log('[CONFIG-EDITOR] Value changed:', {
      fullKey,
      section,
      subKey,
      oldValue,
      newValue,
      deviceId,
      timestamp: new Date().toISOString()
    });
    setEditedConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subKey]: newValue
      }
    }));
    
    // Update flat config and merged params
    setFlatConfig(prev => ({
      ...prev,
      [fullKey]: newValue
    }));
    
    setMergedParams(prev => prev.map(item => 
      item.param === fullKey ? { ...item, value: newValue } : item
    ));
  };

  /**
   * Check if there are any unsaved changes
   */
  const hasUnsavedChanges = useMemo(() => {
    return Object.keys(editedConfig).some(section => {
      const current = editedConfig[section] || {};
      const original = originalConfig[section] || {};
      return Object.keys(current).some(k => current[k] !== original[k]);
    });
  }, [editedConfig, originalConfig]);

  // Notify parent of unsaved changes in workspace mode
  useEffect(() => {
    if (isWorkspaceMode && onUnsavedChanges) {
      onUnsavedChanges(workspaceTabId || deviceIp || deviceId, hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, isWorkspaceMode, onUnsavedChanges]);

  /**
   * Reset a single parameter to original value
   */
  const resetParameter = (section, subKey) => {
    const originalValue = originalConfig[section][subKey];
    handleValueChange(section, subKey, originalValue);
    toast.info('Reset to original value');
  };

  /**
   * Compute changes for a section compared to original config
   */
  const getSectionChanges = (section) => {
    const changes = {};
    const current = editedConfig[section] || {};
    const original = originalConfig[section] || {};
    Object.keys(current).forEach((k) => {
      if (current[k] !== original[k]) changes[k] = current[k];
    });
    return changes;
  };

  /**
   * Save all changes within a section via universal PATCH
   */
  const saveSection = async (section) => {
    const changes = getSectionChanges(section);
    const changeCount = Object.keys(changes).length;
    console.log('[CONFIG-EDITOR] saveSection called:', {
      section,
      changes,
      changeCount,
      deviceId,
      timestamp: new Date().toISOString()
    });
    if (changeCount === 0) {
      console.log('[CONFIG-EDITOR] No changes to save, aborting');
      toast.info('No changes to save for this section');
      return;
    }
    console.log('[CONFIG-EDITOR] Starting section save request');
    setSavingSection((prev) => ({ ...prev, [section]: true }));
    try {
      const payload = {};
      const subKeyByFullKey = {};
      Object.entries(changes).forEach(([subKey, value]) => {
        const fullKey = getFullKey(section, subKey);
        payload[fullKey] = value;
        subKeyByFullKey[fullKey] = subKey;
      });

      const url = `/api/v2/admin/devices/${encodeURIComponent(deviceId)}/settings`;
      console.log('[CONFIG-EDITOR] Sending section save PUT request:', { url, payload });
      
      const res = await authFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      console.log('[CONFIG-EDITOR] Section save response:', { status: res.status, ok: res.ok });
      const result = await res.json().catch(() => ({}));
      console.log('[CONFIG-EDITOR] Section save response body:', result);

      if (!res.ok) {
        throw new Error(result.error || `HTTP ${res.status}`);
      }

      const perParamResults = result?.data?.results || {};
      const successfulSubKeys = [];

      Object.entries(perParamResults).forEach(([paramKey, paramResult]) => {
        const subKey = subKeyByFullKey[paramKey];
        if (subKey && paramResult && paramResult.ok) {
          successfulSubKeys.push(subKey);
        }
      });

      const successCount = successfulSubKeys.length;
      const failCount = changeCount - successCount;

      if (successCount > 0) {
        console.log('[CONFIG-EDITOR] Section save succeeded for keys:', successfulSubKeys);
        setOriginalConfig((prev) => {
          const nextSection = { ...(prev[section] || {}) };
          successfulSubKeys.forEach((subKey) => {
            nextSection[subKey] = changes[subKey];
          });
          return {
            ...prev,
            [section]: nextSection,
          };
        });
      }

      if (failCount > 0 && successCount === 0) {
        throw new Error(result?.error || 'Update failed');
      }

      if (failCount > 0 && successCount > 0) {
        toast.warning(`Partially saved ${successCount} of ${changeCount} changes in ${section}`);
      } else if (successCount > 0) {
        toast.success(`Saved ${successCount} change${successCount > 1 ? 's' : ''} in ${section}`);
      }
    } catch (err) {
      console.error('[CONFIG-EDITOR] Section save error:', err);
      toast.error(`Failed to save ${section}: ${err.message}`);
    } finally {
      console.log('[CONFIG-EDITOR] Section save complete');
      setSavingSection((prev) => ({ ...prev, [section]: false }));
    }
  };

  /**
   * Refresh a single section by reloading config and replacing only that section
   */
  const refreshSection = async (section) => {
    setRefreshingSection((prev) => ({ ...prev, [section]: true }));
    try {
      const basePath = isWorkspaceMode
        ? `/api/v2/admin/devices/tenant-device/${encodeURIComponent(deviceId)}/config`
        : `/api/v2/admin/devices/${encodeURIComponent(deviceId)}/config`;
      const query = isWorkspaceMode && tenantId
        ? `?tenantId=${encodeURIComponent(tenantId)}`
        : '';
      const res = await authFetch(`${basePath}${query}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const parsed = parseAlgoConfig(text);
      const nextSectionData = parsed[section] || {};
      setOriginalConfig((prev) => ({ ...prev, [section]: nextSectionData }));
      setEditedConfig((prev) => ({ ...prev, [section]: nextSectionData }));
      toast.success(`Refreshed ${section}`);
    } catch (err) {
      console.error('Failed to refresh section:', err);
      toast.error(`Failed to refresh ${section}: ${err.message}`);
    } finally {
      setRefreshingSection((prev) => ({ ...prev, [section]: false }));
    }
  };

  /**
   * Check if parameter has been modified
   */
  const isModified = (section, subKey) => {
    const current = editedConfig[section]?.[subKey];
    const original = originalConfig[section]?.[subKey];
    return current !== original;
  };

  /**
   * Get filtered sections based on search
   */
  const filteredSections = useMemo(() => {
    return filterSections(editedConfig, searchQuery);
  }, [editedConfig, searchQuery]);

  /**
   * Get section names for tabs
   */
  const sectionNames = useMemo(() => {
    return Object.keys(filteredSections);
  }, [filteredSections]);

  /**
   * Toggle all sections expanded/collapsed
   */
  const toggleAllSections = () => {
    const allExpanded = Object.keys(expandedSections).length === sectionNames.length;
    if (allExpanded) {
      setExpandedSections({});
    } else {
      const expanded = {};
      sectionNames.forEach(name => { expanded[name] = true; });
      setExpandedSections(expanded);
    }
  };

  /**
   * Render parameter row
   */
  const renderParameterRow = (section, subKey) => {
    const fullKey = getFullKey(section, subKey);
    const value = editedConfig[section][subKey];
    const originalValue = originalConfig[section][subKey];
    const modified = isModified(section, subKey);
    const isEditing = editingCell?.section === section && editingCell?.key === subKey;
    const isApplying = applyingChanges[fullKey];
    const useSelect = shouldUseSelect(value);

    return (
      <TableRow
        key={subKey}
        sx={{
          backgroundColor: modified ? 'rgba(255, 193, 7, 0.1)' : 'inherit',
          '&:hover': { backgroundColor: modified ? 'rgba(255, 193, 7, 0.2)' : 'rgba(0, 0, 0, 0.04)' }
        }}
      >
        {/* Parameter Name */}
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem', width: '40%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {subKey}
            </Typography>
            {modified && (
              <Chip label="Modified" size="small" color="warning" sx={{ height: 20 }} />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {fullKey}
          </Typography>
        </TableCell>

        {/* Value Editor */}
        <TableCell sx={{ width: '45%' }}>
          {useSelect ? (
            <Select
              value={value || ''}
              onChange={(e) => handleValueChange(section, subKey, e.target.value)}
              size="small"
              fullWidth
              sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
            >
              {getSelectOptions(value).map(option => (
                <MenuItem key={option} value={option} sx={{ fontFamily: 'monospace' }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <TextField
              value={value || ''}
              onChange={(e) => handleValueChange(section, subKey, e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                '& input': { fontFamily: 'monospace', fontSize: '0.875rem' }
              }}
            />
          )}
          {modified && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Original: <code>{originalValue}</code>
            </Typography>
          )}
        </TableCell>

        {/* Actions */}
        <TableCell sx={{ width: '15%' }}>
          <Stack direction="row" spacing={1}>
            {modified && (
              <>
                <Tooltip title="Apply change">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => applyChange(section, subKey)}
                    disabled={isApplying}
                  >
                    {isApplying ? <CircularProgress size={20} /> : <SaveIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset to original">
                  <IconButton
                    size="small"
                    color="default"
                    onClick={() => resetParameter(section, subKey)}
                    disabled={isApplying}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  /**
   * Render section table
   */
  const renderSectionTable = (section) => {
    const params = filteredSections[section];
    if (!params || Object.keys(params).length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No parameters in this section match your search.
        </Alert>
      );
    }

    // Compute modified count for section
    const current = editedConfig[section] || {};
    const original = originalConfig[section] || {};
    const changedCount = Object.keys(current).reduce((acc, k) => (current[k] !== original[k] ? acc + 1 : acc), 0);

    return (
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={savingSection[section] ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={() => saveSection(section)}
            disabled={changedCount === 0 || !!savingSection[section]}
          >
            {changedCount === 0 ? 'No Changes' : `Save Changes (${changedCount})`}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={refreshingSection[section] ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => refreshSection(section)}
            disabled={!!refreshingSection[section]}
          >
            Refresh Section
          </Button>
        </Stack>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Parameter</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(params).map(subKey => renderParameterRow(section, subKey))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  /**
   * Render a single section card
   */
  const renderSectionCard = (section) => {
    const params = filteredSections[section] || {};
    const paramKeys = Object.keys(params);
    const current = editedConfig[section] || {};
    const original = originalConfig[section] || {};
    const changedCount = paramKeys.reduce((acc, k) => (current[k] !== original[k] ? acc + 1 : acc), 0);
    const isSaving = savingSection[section];
    const isRefreshing = refreshingSection[section];

    return (
      <Card
        key={section}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'box-shadow 0.3s',
          '&:hover': { boxShadow: 4 }
        }}
      >
        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" component="div">
              {getSectionDisplayName(section)}
            </Typography>
            <Chip
              label={`${paramKeys.length} param${paramKeys.length !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Stack>
          
          {changedCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
              {changedCount} unsaved change{changedCount !== 1 ? 's' : ''}
            </Alert>
          )}

          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {paramKeys.map(subKey => {
              const value = current[subKey];
              const originalValue = original[subKey];
              const modified = current[subKey] !== original[subKey];
              const useSelect = shouldUseSelect(value);
              
              return (
                <Box
                  key={subKey}
                  sx={{
                    py: 1,
                    px: 1.5,
                    mb: 1,
                    borderRadius: 1,
                    backgroundColor: modified ? 'rgba(255, 193, 7, 0.1)' : 'transparent',
                    '&:hover': { backgroundColor: modified ? 'rgba(255, 193, 7, 0.2)' : 'rgba(0, 0, 0, 0.04)' },
                    border: '1px solid',
                    borderColor: modified ? 'warning.main' : 'divider'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    {/* Parameter Name */}
                    <Box sx={{ minWidth: '35%', maxWidth: '35%' }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: modified ? 600 : 400 }}>
                          {subKey}
                        </Typography>
                        {modified && (
                          <Chip label="Modified" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Stack>
                    </Box>
                    
                    {/* Value Input */}
                    <Box sx={{ flexGrow: 1 }}>
                      {useSelect ? (
                        <Select
                          value={value || ''}
                          onChange={(e) => handleValueChange(section, subKey, e.target.value)}
                          size="small"
                          fullWidth
                          sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                        >
                          {getSelectOptions(value).map(option => (
                            <MenuItem key={option} value={option} sx={{ fontFamily: 'monospace' }}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <TextField
                          value={value || ''}
                          onChange={(e) => handleValueChange(section, subKey, e.target.value)}
                          size="small"
                          fullWidth
                          variant="outlined"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            '& input': { fontFamily: 'monospace', fontSize: '0.875rem' }
                          }}
                        />
                      )}
                      {modified && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
                          Original: <code>{originalValue}</code>
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </CardContent>
        
        <Divider />
        
        <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={() => saveSection(section)}
            disabled={changedCount === 0 || isSaving}
          >
            {changedCount === 0 ? 'No Changes' : `Save (${changedCount})`}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => refreshSection(section)}
            disabled={isRefreshing}
          >
            Refresh
          </Button>
        </CardActions>
      </Card>
    );
  };

  /**
   * Render metadata-driven section card with grouped parameters
   */
  const renderMetadataSectionCard = (sectionName) => {
    // Filter params by parameter search query first
    let filtered = searchQuery ? filterParameters(mergedParams, searchQuery) : mergedParams;
    
    // Filter by value search query
    filtered = valueSearchQuery ? filterParametersByValue(filtered, valueSearchQuery) : filtered;
    
    // Filter params for this section
    const sectionParams = filtered.filter(item => {
      const itemSection = item.meta?.section || 'Uncategorized';
      return itemSection === sectionName && isParameterVisible(item.meta, flatConfig);
    });

    if (sectionParams.length === 0) return null;

    // Group by group within section
    const groups = {};
    sectionParams.forEach(item => {
      const group = item.meta?.group || 'General';
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    });

    // Count changes
    const changedCount = sectionParams.reduce((acc, item) => {
      return originalFlatConfig[item.param] !== item.value ? acc + 1 : acc;
    }, 0);

    // Determine section key for saving (use first param's section prefix)
    const firstParam = sectionParams[0]?.param || '';
    const sectionKey = firstParam.split('.')[0] || 'general';
    const isSaving = savingSection[sectionKey];
    const isRefreshing = refreshingSection[sectionKey];

    return (
      <Card
        key={sectionName}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'box-shadow 0.3s',
          '&:hover': { boxShadow: 4 }
        }}
      >
        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" component="div">
              {sectionName}
            </Typography>
            <Chip
              label={`${sectionParams.length} param${sectionParams.length !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Stack>
          
          {changedCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
              {changedCount} unsaved change{changedCount !== 1 ? 's' : ''}
            </Alert>
          )}

          <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
            {Object.entries(groups).map(([groupName, groupParams]) => (
              <Box key={groupName} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                  {groupName}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {groupParams.map(item => {
                  const originalFlat = flattenConfig(originalConfig);
                  const modified = originalFlat[item.param] !== item.value;
                  const [section, ...subKeyParts] = item.param.split('.');
                  const subKey = subKeyParts.join('.');
                  
                  return (
                    <Box
                      key={item.param}
                      sx={{
                        py: 1.5,
                        px: 2,
                        mb: 2,
                        borderRadius: 1,
                        backgroundColor: modified ? 'rgba(255, 193, 7, 0.1)' : 'transparent',
                        '&:hover': { backgroundColor: modified ? 'rgba(255, 193, 7, 0.2)' : 'rgba(0, 0, 0, 0.04)' },
                        border: '1px solid',
                        borderColor: modified ? 'warning.main' : 'divider'
                      }}
                    >
                      {modified && (
                        <Chip label="Modified" size="small" color="warning" sx={{ mb: 1, height: 18, fontSize: '0.65rem' }} />
                      )}
                      <FieldRenderer
                        param={item.param}
                        value={item.value}
                        meta={item.meta}
                        deviceConfig={flatConfig}
                        onChange={(newValue) => handleValueChange(section, subKey, newValue)}
                        showOriginal={modified}
                        originalValue={originalFlat[item.param]}
                      />
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </CardContent>
        
        <Divider />
        
        <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={() => saveSection(sectionKey)}
            disabled={changedCount === 0 || isSaving}
          >
            {changedCount === 0 ? 'No Changes' : `Save (${changedCount})`}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => refreshSection(sectionKey)}
            disabled={isRefreshing}
          >
            Refresh
          </Button>
        </CardActions>
      </Card>
    );
  };

  /**
   * Get filtered and visible sections from metadata
   */
  const metadataSections = useMemo(() => {
    // Filter by parameter search query
    let filtered = searchQuery ? filterParameters(mergedParams, searchQuery) : mergedParams;
    
    // Filter by value search query
    filtered = valueSearchQuery ? filterParametersByValue(filtered, valueSearchQuery) : filtered;
    
    // Get unique sections
    const sections = new Set();
    filtered.forEach(item => {
      const section = item.meta?.section || 'Uncategorized';
      if (isParameterVisible(item.meta, flatConfig)) {
        sections.add(section);
      }
    });
    
    return Array.from(sections).sort();
  }, [mergedParams, searchQuery, valueSearchQuery, flatConfig]);

  /**
   * Render content based on view mode
   */
  const renderContent = () => {
    if (viewMode === 'cards') {
      // Cards view (default) - Use metadata-driven rendering
      return (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {metadataSections.map(section => (
            <Grid item xs={12} md={6} key={section}>
              {renderMetadataSectionCard(section)}
            </Grid>
          ))}
        </Grid>
      );
    } else if (viewMode === 'accordion') {
      // Accordion view - Use metadata-driven rendering
      return (
        <Box sx={{ mt: 2 }}>
          {metadataSections.map(section => {
            // Count params in this section
            let filtered = searchQuery ? filterParameters(mergedParams, searchQuery) : mergedParams;
            filtered = valueSearchQuery ? filterParametersByValue(filtered, valueSearchQuery) : filtered;
            const sectionParamCount = filtered.filter(item => {
              const itemSection = item.meta?.section || 'Uncategorized';
              return itemSection === section && isParameterVisible(item.meta, flatConfig);
            }).length;
            
            return (
              <Accordion
                key={section}
                expanded={expandedSections[section] || false}
                onChange={() => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">{section}</Typography>
                  <Chip
                    label={sectionParamCount}
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  {renderMetadataSectionCard(section)}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      );
    } else {
      // Table view (tabs) - Use metadata-driven rendering
      return (
        <>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}
          >
            {metadataSections.map((section, index) => {
              // Count params in this section
              let filtered = searchQuery ? filterParameters(mergedParams, searchQuery) : mergedParams;
              filtered = valueSearchQuery ? filterParametersByValue(filtered, valueSearchQuery) : filtered;
              const sectionParamCount = filtered.filter(item => {
                const itemSection = item.meta?.section || 'Uncategorized';
                return itemSection === section && isParameterVisible(item.meta, flatConfig);
              }).length;
              
              return (
                <Tab
                  key={section}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {section}
                      <Chip
                        label={sectionParamCount}
                        size="small"
                        sx={{ height: 20 }}
                      />
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
          {metadataSections[selectedTab] && renderMetadataSectionCard(metadataSections[selectedTab])}
        </>
      );
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadConfig} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: isWorkspaceMode ? '100%' : '100vh', overflowY: 'scroll', WebkitOverflowScrolling: 'touch' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        {!isWorkspaceMode && (
          <Breadcrumbs sx={{ mb: 2 }}>
            <MuiLink
              component="button"
              variant="body1"
              onClick={() => navigate('/admin/event-system/devices')}
              sx={{ cursor: 'pointer', textDecoration: 'none' }}
            >
              Devices
            </MuiLink>
            <Typography color="text.primary">Config Editor</Typography>
          </Breadcrumbs>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Configuration Editor
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Device: <strong>{deviceName}</strong> {deviceIp && `(${deviceIp})`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadConfig}
            >
              Reload
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/admin/event-system/devices')}
            >
              Back to Devices
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder="Search parameters..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <TextField
            placeholder="Search values..."
            value={valueSearchQuery}
            onChange={(e) => setValueSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="cards">
              <Tooltip title="Cards View">
                <CardsIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table">
              <Tooltip title="Table View">
                <TableIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="accordion">
              <Tooltip title="Accordion View">
                <AccordionIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          {viewMode === 'accordion' && (
            <Button
              variant="outlined"
              size="small"
              onClick={toggleAllSections}
              startIcon={Object.keys(expandedSections).length === sectionNames.length ? <CollapseIcon /> : <ExpandIcon />}
            >
              {Object.keys(expandedSections).length === sectionNames.length ? 'Collapse All' : 'Expand All'}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Content */}
      {(viewMode === 'cards' ? metadataSections.length === 0 : sectionNames.length === 0) ? (
        <Alert severity="info">
          No parameters match your search query.
        </Alert>
      ) : (
        renderContent()
      )}
    </Box>
  );
}
